import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getSellerProfile, getSellerStats, getListings, getSellerAllRatings } from '../../services/api';
import { getImageUrl } from '../../utils/imageUtils';
import { formatPrice, formatDate } from '../../utils/listingUtils';
import Loading from '../../components/common/Loading';
import {Star} from 'lucide-react';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';

// Import sub-components
import MobileSellerDetail from './MobileSellerDetail';
import DesktopSellerDetail from './DesktopSellerDetail';

const SellerDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [seller, setSeller] = useState(null);
  const [stats, setStats] = useState(null);
  const [listings, setListings] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('listings');
  const [loading, setLoading] = useState(true);
  const [listingsFilter, setListingsFilter] = useState('active');
  const [showAllReviews, setShowAllReviews] = useState(false);
  const [isFavorited, setIsFavorited] = useState(false);
  
  // ProductGrid state
  const [viewMode, setViewMode] = useState('grid');
  const [sortBy, setSortBy] = useState('newest');
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    fetchSellerData();
  }, [id]);

  const fetchSellerData = async () => {
    setLoading(true);
    try {
      const sellerResponse = await getSellerProfile(id);
      setSeller(sellerResponse.data?.seller || sellerResponse);

      const statsResponse = await getSellerStats(id);
      setStats(statsResponse.data || statsResponse);

      const listingsResponse = await getListings({ 
        seller_id: id, 
        status: 'active',
      });
      setListings(listingsResponse.data || listingsResponse || []);

    } catch (error) {
      // console.error('Error fetching seller data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch reviews when Reviews tab is active
  useEffect(() => {
    if (activeTab === 'reviews' && seller) {
      fetchSellerReviews();
    }
  }, [activeTab, seller]);

  const fetchSellerReviews = async () => {
    if (!seller) return;
    
    setReviewsLoading(true);
    try {
      const reviewsResponse = await getSellerAllRatings(seller.user_id || seller.seller_id, 50, 0);
      
      if (reviewsResponse.success && reviewsResponse.data) {
        setReviews(reviewsResponse.data);
      } else if (Array.isArray(reviewsResponse)) {
        setReviews(reviewsResponse);
      } else {
        setReviews([]);
      }
    } catch (error) {
      // console.error('Error fetching seller reviews:', error);
      setReviews([]);
    } finally {
      setReviewsLoading(false);
    }
  };

  const formatReviewData = (review) => {
    return {
      ...review,
      rating_id: review.rating_id || review.id,
      rating_overall: review.rating_overall || 0,
      rating_quality: null,
      rating_communication: null,
      rating_delivery: null,
      review_text: review.review_text || '',
      buyer_name: review.buyer_first_name && review.buyer_last_name 
        ? `${review.buyer_first_name} ${review.buyer_last_name}`
        : review.buyer_name || review.full_name || 'Anonymous',
      buyer_profile_picture: review.buyer_profile_picture || review.profile_picture_url || '',
      is_verified_purchase: false,
      created_at: review.created_at || review.review_date,
      status: review.status || 'approved',
      helpful_count: review.helpful_count || 0,
      listing_title: review.listing_title || 'Item',
      time_ago: review.time_ago || formatTimeAgo(review.created_at)
    };
  };

  const formatTimeAgo = (dateString) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 60) {
      return `${diffMins}m ago`;
    } else if (diffHours < 24) {
      return `${diffHours}h ago`;
    } else if (diffDays < 30) {
      return `${diffDays}d ago`;
    } else {
      return formatDate(dateString);
    }
  };

  const handleContactSeller = () => {
    if (!seller) return;
    
    if (listings.length > 0) {
      navigate(`/listings/${listings[0].listing_id}`);
    } else {
      alert(`Contact ${seller.store_name || seller.first_name} via message`);
    }
  };

  const handleShare = () => {
    if (!seller) return;
    
    const shareUrl = `${window.location.origin}/seller/${id}`;
    const shareText = `Check out ${seller.store_name || seller.first_name}'s profile on EcoMart!`;
    
    if (navigator.share) {
      navigator.share({
        title: `${seller.store_name || seller.first_name}'s Profile`,
        text: shareText,
        url: shareUrl,
      });
    } else {
      navigator.clipboard.writeText(shareUrl)
        .then(() => alert('Profile link copied to clipboard!'))
    }
  };

  const handleToggleFavorite = () => {
    if (!seller) return;
    setIsFavorited(!isFavorited);
  };

  const handleViewListing = (listingId) => {
    navigate(`/listings/${listingId}`);
  };

  const getSafeRating = (ratingValue) => {
    if (ratingValue === null || ratingValue === undefined || isNaN(ratingValue)) {
      return 0;
    }
    
    const numericRating = typeof ratingValue === 'string' ? parseFloat(ratingValue) : ratingValue;
    return isNaN(numericRating) ? 0 : numericRating;
  };

  const renderStars = (rating, size = 'w-5 h-5') => {
    const safeRating = getSafeRating(rating);
    
    return (
      <div className="flex items-center">
        {[...Array(5)].map((_, index) => (
          <Star
            key={index}
            className={`${size} ${
              index < Math.floor(safeRating)
                ? 'fill-current text-yellow-400'
                : 'text-gray-300'
            }`}
          />
        ))}
        <span className="ml-2 font-bold text-black">
          {safeRating.toFixed(1)}
        </span>
      </div>
    );
  };

  const calculateAverageRating = () => {
    if (reviews.length === 0) return 0;
    
    const validRatings = reviews
      .map(r => r.rating_overall)
      .filter(rating => rating && rating > 0);
    
    if (validRatings.length === 0) return 0;
    
    const total = validRatings.reduce((sum, rating) => sum + rating, 0);
    return (total / validRatings.length).toFixed(1);
  };

  const getTotalReviewsCount = () => {
    return stats?.summary?.total_ratings || reviews.length || 0;
  };

  const getRecentReviewsCount = () => {
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    
    return reviews.filter(review => {
      const reviewDate = new Date(review.created_at);
      return reviewDate > thirtyDaysAgo;
    }).length;
  };

  const getFilteredListings = () => {
    let filtered = [...listings];
    
    switch (listingsFilter) {
      case 'featured':
        filtered = filtered.filter(listing => listing.is_boosted);
        break;
      case 'recent':
        filtered = filtered.sort((a, b) => 
          new Date(b.created_at) - new Date(a.created_at)
        );
        break;
      case 'active':
      default:
        break;
    }
    
    return filtered;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loading text="Loading seller profile..." />
      </div>
    );
  }

  if (!seller) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-black mb-4">Seller Not Found</h2>
          <p className="text-gray-600 mb-6">The seller profile you're looking for doesn't exist.</p>
          <button
            onClick={() => navigate(-1)}
            className="bg-[#ff660e] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#ff5500]"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const sellerName = seller.store_name || `${seller.first_name} ${seller.last_name}`;
  const rating = getSafeRating(stats?.summary?.avg_overall || calculateAverageRating());
  const totalReviews = getTotalReviewsCount();
  const recentReviews = getRecentReviewsCount();
  const filteredListings = getFilteredListings();
  // Shared props for both mobile and desktop components
  const sharedProps = {
    seller,
    sellerName,
    rating,
    totalReviews,
    recentReviews,
    listings,
    filteredListings,
    reviews,
    reviewsLoading,
    activeTab,
    setActiveTab,
    listingsFilter,
    setListingsFilter,
    showAllReviews,
    setShowAllReviews,
    isFavorited,
    viewMode,
    setViewMode,
    sortBy,
    setSortBy,
    currentPage,
    setCurrentPage,
    stats,
    handleContactSeller,
    handleShare,
    handleToggleFavorite,
    handleViewListing,
    renderStars,
    formatReviewData,
    formatPrice,
    formatDate,
    getImageUrl,
    calculateAverageRating,
    navigate
  };

  return (
    <>
      <MobileSellerDetail {...sharedProps} />
      <DesktopSellerDetail {...sharedProps} />
    </>
  );
};

export default SellerDetail;